import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, Checkbox, Details, Dialog, IconButton, List, ListItem, Skeleton, StatusChip,
  StepList, TextField, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, type components } from '../lib/api';
import { durata } from '../lib/attivita';
import { BarraUtente } from '../shell';
import { Back, Copy } from '../icons';
import { Anello, CaricoSezione, Logo } from '../brand';
import { peso } from './backup-copertura';
import { localeIntl, t } from '../lib/lingua';

type Trasloco = components['schemas']['Trasloco'];
type RepoTrasloco = components['schemas']['RepoTrasloco'];
type Indirizzo = components['schemas']['Indirizzo'];
type Controllo = components['schemas']['ControlloTrasloco'];
type RepoCreato = components['schemas']['AddonRepoCreato'];

/**
 * Un trasloco, dalla preparazione alla chiusura.
 *
 * La pagina è la guida: in ogni fase dice cosa sta succedendo, cosa manca e
 * cosa si può fare, e i passi che non stanno nel pannello — la chiave su
 * GitHub, il record DNS del cliente — li mostra pronti da copiare e li
 * verifica.
 */

const FASI: Array<{ fase: string[]; titolo: string }> = [
  { fase: ['preparing'], titolo: 'Preparazione' },
  { fase: ['trial', 'ready'], titolo: 'Prova|trasloco' },
  { fase: ['cutover'], titolo: 'Trasloco' },
  { fase: ['addresses'], titolo: 'Indirizzi' },
  { fase: ['checks'], titolo: 'Controlli' },
  { fase: ['done'], titolo: 'Chiuso' },
];

export const NOME_FASE: Record<string, { label: string; tone: 'success' | 'error' | 'running' | 'neutral' }> = {
  preparing: { label: 'In preparazione', tone: 'neutral' },
  trial: { label: 'Prova in corso', tone: 'running' },
  ready: { label: 'Prova pronta', tone: 'neutral' },
  cutover: { label: 'Trasloco', tone: 'running' },
  addresses: { label: 'Indirizzi', tone: 'running' },
  checks: { label: 'Controlli', tone: 'running' },
  done: { label: 'Traslocata', tone: 'success' },
  cancelled: { label: 'Annullato', tone: 'neutral' },
};

/** «1 modulo installato», «3 moduli installati». */
export const moduliInstallati = (n: number) => (n === 1 ? t('1 modulo installato') : t('{n} moduli installati', { n }));

const vivo = (j?: components['schemas']['StatoJob']) => !!j && (j.status === 'queued' || j.status === 'running');

export function TraslocoPage() {
  const { slug, id } = useParams({ from: '/shell/orgs/$slug/traslochi/$id' });
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['migration', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/migrations/{id}', { params: { path: { slug, id } } })),
    refetchInterval: (r) => {
      const tr = r.state.data;
      if (tr && (vivo(tr.job) || vivo(tr.importJob) || tr.phase === 'trial' || tr.phase === 'cutover')) return 3000;
      // Un server nuovo si installa in una decina di minuti: si guarda finché è pronto.
      return tr && tr.phase === 'preparing' && tr.serverStatus !== 'ready' ? 15_000 : false;
    },
  });
  const tr = q.data;

  return (
    <>
      <TopAppBar
        leading={
          <IconButton label={t('Torna ai traslochi')} onClick={() => navigate({ to: '/orgs/$slug/traslochi', params: { slug } })}>
            <Back />
          </IconButton>
        }
        title={tr ? t('Trasloco di {nome}', { nome: tr.name }) : t('Trasloco')}
        subtitle={tr ? `${tr.sourceName} · ${tr.sourceInstance} → ${tr.serverName}` : undefined}
        actions={<BarraUtente slug={slug} />}
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {q.error && <Banner tone="error" title={t('Non riesco a leggere il trasloco')}>{q.error.message}</Banner>}
          {q.isPending && <Skeleton height="16rem" />}
          {tr && (
            <>
              <Card variant="filled">
                <StepList compact items={passi(tr)} />
              </Card>
              <Lavori slug={slug} tr={tr} />
              {(tr.phase === 'preparing' || tr.phase === 'ready') && <Preparazione slug={slug} tr={tr} />}
              {tr.phase === 'cutover' && !vivo(tr.job) && !vivo(tr.importJob) && <TraslocoFermo slug={slug} tr={tr} />}
              {tr.phase === 'addresses' && <Indirizzi slug={slug} tr={tr} collega />}
              {tr.phase === 'checks' && <Controlli slug={slug} tr={tr} />}
              {tr.phase === 'done' && <Fatto slug={slug} tr={tr} />}
              {tr.phase === 'cancelled' && (
                <Banner title={t('Trasloco annullato')}>{t('L’istanza resta su CloudPepper, com’era.')}</Banner>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function passi(tr: Trasloco) {
  const i = FASI.findIndex((f) => f.fase.includes(tr.phase));
  const fallito = tr.job?.status === 'failed' || tr.job?.status === 'cancelled';
  return FASI.map((f, n) => ({
    title: t(f.titolo),
    state: (tr.phase === 'done' || n < i ? 'done' : n === i ? (fallito && tr.phase === 'cutover' ? 'failed' : 'current') : 'pending') as
      'done' | 'current' | 'pending' | 'failed',
  }));
}

/** Il lavoro in corso, o l'ultimo andato storto: con il link al suo log. */
function Lavori({ slug, tr }: { slug: string; tr: Trasloco }) {
  const navigate = useNavigate();
  const log = (id: string) => navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id } });
  const cosa: Record<string, string> = {
    migration_trial: t('Prova in corso: porto il database e gli allegati, poi l’import'),
    migration_cutover: t('Trasloco in corso: CloudPepper è spento, porto i dati di adesso'),
    migration_revert: t('Torno su CloudPepper'),
  };
  if (vivo(tr.job) || vivo(tr.importJob)) {
    const importa = vivo(tr.importJob) && !!tr.importJob;
    return (
      <Card variant="outlined">
        <div className="md-attesa">
          <Logo size={48} anim="livello" label={t('Trasloco in corso')} />
          <p className="md-body-medium" style={{ margin: 0 }}>
            {importa ? t('L’istanza sta nascendo su Molo con i dati di CloudPepper.') : cosa[tr.job?.kind ?? ''] ?? t('Al lavoro')}
          </p>
          <div className="md-stack__inline">
            {tr.job && <Button variant="text" onClick={() => log(tr.job!.id)}>{t('Segui il trasloco')}</Button>}
            {importa && <Button variant="text" onClick={() => log(tr.importJob!.id)}>{t('Segui l’import')}</Button>}
          </div>
        </div>
      </Card>
    );
  }
  if (tr.job && (tr.job.status === 'failed' || tr.job.status === 'cancelled') && tr.phase !== 'done' && tr.phase !== 'cancelled') {
    return (
      <Banner
        tone="error"
        title={t('L’ultimo passo non è andato')}
        action={<Button variant="text" onClick={() => log(tr.job!.id)}>{t('Apri il log')}</Button>}
      >
        {tr.job.error}
      </Banner>
    );
  }
  return null;
}

function useAzione(slug: string, id: string, percorso: string, ok?: string) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  return useMutation({
    mutationFn: () =>
      must(api.POST(`/api/v1/orgs/{slug}/migrations/{id}/${percorso}` as '/api/v1/orgs/{slug}/migrations/{id}/trial', {
        params: { path: { slug, id } },
      })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['migration', slug, id] });
      void qc.invalidateQueries({ queryKey: ['migrations', slug] });
      if (ok) avvisa(ok);
    },
  });
}

/** Prima del trasloco vero: chiavi, rami, librerie, nome, e la prova. */
function Preparazione({ slug, tr }: { slug: string; tr: Trasloco }) {
  const navigate = useNavigate();
  const verifica = useAzione(slug, tr.id, 'verify');
  const prova = useAzione(slug, tr.id, 'trial', t('Parte la prova'));
  const annulla = useAzione(slug, tr.id, 'cancel', t('Trasloco annullato'));
  const [trasloca, setTrasloca] = useState(false);
  const repos = tr.spec.repos ?? [];
  // Le cartelle che su CloudPepper non hanno git aspettano il loro
  // repository; gli altri la verifica.
  const daCollegare = repos.filter((r) => !r.url);
  const daVerificare = repos.filter((r) => r.url && !r.verifiedAt);
  const occupato = vivo(tr.job) || vivo(tr.importJob);
  // Verifica e prova si fanno dal server di destinazione: finché non è pronto
  // si prepara il resto, a cominciare dalle chiavi su GitHub.
  const serverPronto = tr.serverStatus === 'ready';

  return (
    <>
      {!serverPronto && <ServerInArrivo slug={slug} tr={tr} />}
      <section className="md-stack">
        <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Cosa si porta')}</h2>
        <p className="md-body-medium" style={{ margin: 0 }}>
          Odoo {tr.spec.major}{tr.spec.enterprise ? ' Enterprise' : ''} ·{' '}
          {t('database {db} · allegati {allegati}', { db: peso(tr.spec.dbBytes), allegati: peso(tr.spec.filestoreBytes) })}
        </p>
        <Nome slug={slug} tr={tr} />
      </section>

      {repos.length > 0 && (
        <section className="md-stack">
          <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Repository dei moduli')}</h2>
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Su GitHub, per ogni repository: {percorso}, incolla la chiave, lascia la sola lettura. Poi «Verifica»: Molo prova dal server di destinazione che la chiave apra il repository, che il ramo ci sia e che ci siano i moduli installati.', {
              percorso: <b>Settings → Deploy keys → Add deploy key</b>,
            })}
            {daCollegare.length > 0 && (
              <> {t('Le cartelle senza git — CloudPepper porta sul server il codice senza la storia — chiedono prima da quale repository vengono.')}</>
            )}
          </p>
          {repos.map((r) => <Repository key={r.sourceDir || r.url} slug={slug} tr={tr} r={r} />)}
          {verifica.error && <Banner tone="error" title={t('Non ho verificato')}>{(verifica.error as Error).message}</Banner>}
          <div className="md-stack__inline">
            <Button variant="tonal" icon={verifica.isPending ? <Anello /> : undefined} disabled={verifica.isPending || !serverPronto} onClick={() => verifica.mutate()}>
              {t('Verifica')}
            </Button>
          </div>
        </section>
      )}

      <Librerie slug={slug} tr={tr} />
      <section className="md-stack">
        <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Indirizzi')}</h2>
        <Indirizzi slug={slug} tr={tr} />
      </section>

      <section className="md-stack">
        <h2 className="md-title-medium" style={{ margin: 0 }}>{tr.phase === 'ready' ? t('La prova') : t('Prima, una prova')}</h2>
        {tr.phase === 'ready' && tr.trialInstance ? (
          <Banner
            tone="success"
            title={tr.trialSeconds ? t('Prova pronta in {durata}', { durata: durata(tr.trialSeconds) }) : t('Prova pronta')}
            action={
              <Button variant="text" onClick={() => navigate({ to: '/orgs/$slug/instances/$id', params: { slug, id: tr.trialInstance!.id } })}>
                {t('Apri la prova')}
              </Button>
            }
          >
            {tr.trialSeconds ? `${t('È il fermo che avrà il cliente il giorno del trasloco.')} ` : ''}
            {t('La prova è neutralizzata — cron spenti, posta disattivata — e CloudPepper è ancora acceso: entra con «Entra in Odoo», guarda dati, allegati, report e moduli.')}
          </Banner>
        ) : (
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Molo fa il dump con CloudPepper acceso e crea {nome}, neutralizzata. Si guarda che sia tutto al suo posto, e si misura quanto ci vuole.', {
              nome: <span className="md-mono">{tr.name}-trasloco</span>,
            })}
          </p>
        )}
        {daCollegare.length > 0 ? (
          <Banner title={t('Prima i repository delle cartelle')}>
            {t('Da collegare: {elenco}.', { elenco: daCollegare.map((r) => r.suggestedName || r.sourceDir).join(', ') })}
          </Banner>
        ) : daVerificare.length > 0 && (
          <Banner title={t('Prima le chiavi')}>
            {t('Mancano da verificare: {elenco}.', { elenco: daVerificare.map((r) => r.url).join(', ') })}
          </Banner>
        )}
        {prova.error && <Banner tone="error" title={t('Non è partita')}>{(prova.error as Error).message}</Banner>}
        {annulla.error && <Banner tone="error" title={t('Non l’ho annullato')}>{(annulla.error as Error).message}</Banner>}
        <div className="md-stack__inline">
          <Button
            variant={tr.phase === 'ready' ? 'tonal' : 'filled'}
            icon={prova.isPending ? <Anello /> : undefined}
            disabled={occupato || prova.isPending || daVerificare.length > 0 || daCollegare.length > 0 || !serverPronto}
            onClick={() => prova.mutate()}
          >
            {tr.phase === 'ready' ? t('Rifai la prova') : t('Fai la prova')}
          </Button>
          {tr.phase === 'ready' && tr.trialInstance && (
            <Button variant="filled" disabled={occupato} onClick={() => setTrasloca(true)}>{t('Trasloca…')}</Button>
          )}
          <Button variant="text" disabled={occupato || annulla.isPending} onClick={() => annulla.mutate()}>{t('Annulla il trasloco')}</Button>
        </div>
      </section>
      <ConfermaTrasloco slug={slug} tr={tr} aperto={trasloca} chiudi={() => setTrasloca(false)} />
    </>
  );
}

/** La destinazione che non è ancora pronta: di solito il server comprato aprendo il trasloco. */
function ServerInArrivo({ slug, tr }: { slug: string; tr: Trasloco }) {
  const navigate = useNavigate();
  const guasto = ['error', 'decommissioning', 'released'].includes(tr.serverStatus);
  return (
    <Banner
      tone={guasto ? 'error' : 'info'}
      title={guasto ? t('Il server {nome} non è pronto', { nome: tr.serverName }) : t('Il server {nome} si sta preparando', { nome: tr.serverName })}
      action={
        <Button variant="text" onClick={() => navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: tr.serverId } })}>
          {t('Apri il server')}
        </Button>
      }
    >
      {guasto
        ? t('L’installazione si è fermata: riprendila dalla scheda del server, o annulla il trasloco e aprilo verso un altro.')
        : t('Di solito ci vuole una decina di minuti. Intanto incolla le chiavi su GitHub e controlla nome e librerie: «Verifica» e la prova partono quando il server è pronto.')}
    </Banner>
  );
}

function Nome({ slug, tr }: { slug: string; tr: Trasloco }) {
  const qc = useQueryClient();
  const [nome, setNome] = useState(tr.name);
  useEffect(() => setNome(tr.name), [tr.name]);
  const salva = useMutation({
    mutationFn: () =>
      must(api.PATCH('/api/v1/orgs/{slug}/migrations/{id}', { params: { path: { slug, id: tr.id } }, body: { name: nome.trim() } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['migration', slug, tr.id] }),
  });
  return (
    <div className="md-stack">
      <TextField
        label={t('Nome su Molo')}
        value={nome}
        onChange={(e) => setNome(e.target.value.toLowerCase())}
        help={t('Diventa l’indirizzo sotto il dominio dell’organizzazione. Se è quello che CloudPepper usa già, il giorno del trasloco Molo lo sposta da solo.')}
      />
      {salva.error && <Banner tone="error" title={t('Non l’ho cambiato')}>{(salva.error as Error).message}</Banner>}
      {nome.trim() !== tr.name && (
        <div className="md-stack__inline">
          <Button variant="tonal" icon={salva.isPending ? <Anello /> : undefined} disabled={salva.isPending || !nome.trim()} onClick={() => salva.mutate()}>
            {t('Salva il nome')}
          </Button>
        </div>
      )}
    </div>
  );
}

function Repository({ slug, tr, r }: { slug: string; tr: Trasloco; r: RepoTrasloco }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [ramo, setRamo] = useState(r.branch);
  const [url, setUrl] = useState(r.url);
  useEffect(() => { setRamo(r.branch); setUrl(r.url); }, [r.branch, r.url]);
  // Una cartella senza git si collega a un repository per cartella; un
  // repository vero cambia solo il ramo.
  const cartella = r.sourceDir ?? '';
  const salva = useMutation({
    mutationFn: () =>
      must(api.PATCH('/api/v1/orgs/{slug}/migrations/{id}', {
        params: { path: { slug, id: tr.id } },
        body: cartella
          ? { folders: { [cartella]: { url: url.trim(), branch: ramo.trim() } } }
          : { branches: { [r.url]: ramo.trim() } },
      })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['migration', slug, tr.id] }),
  });
  const cambiato = ramo.trim() !== r.branch || (cartella !== '' && url.trim() !== r.url);
  return (
    <Card variant="outlined">
      <div className="md-stack">
        <div className="md-section-head">
          <span className="md-mono md-body-medium">{r.url || nomeDellaCartella(cartella)}</span>
          {!r.url
            ? <StatusChip status="todo" label={t('Da collegare')} tone="neutral" />
            : r.verifiedAt
              ? <StatusChip status="ok" label={t('Verificato')} tone="success" />
              : <StatusChip status="todo" label={r.error ? t('Non entra') : t('Da verificare')} tone={r.error ? 'error' : 'neutral'} />}
        </div>
        {r.error && <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-error)' }}>{r.error}</p>}
        <p className="md-body-small" style={{ margin: 0 }}>
          {moduliInstallati((r.modules ?? []).length)}{(r.modules ?? []).length > 0 ? `: ${(r.modules ?? []).join(', ')}` : ''}
          {r.subdir ? ` · ${t('cartella {cartella}', { cartella: r.subdir })}` : ''}
        </p>
        {cartella && (
          <>
            <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
              {r.url
                ? t('Su CloudPepper sta in {cartella}, senza git.', { cartella: <span className="md-mono">{cartella}</span> })
                : t('Su CloudPepper sta in {cartella}, senza git: scrivi da quale repository GitHub viene, e con quale ramo.', {
                  cartella: <span className="md-mono">{cartella}</span>,
                })}
            </p>
            <TextField
              label={t('Repository su GitHub')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('git@github.com:<proprietario>/{nome}.git', { nome: r.suggestedName || 'repository' })}
            />
          </>
        )}
        {r.url && (
          <>
            <pre className="md-log md-code" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{r.publicKey}</pre>
            <div className="md-stack__inline">
              <Button variant="text" icon={<Copy />} onClick={() => { void navigator.clipboard?.writeText(r.publicKey); avvisa(t('Chiave copiata')); }}>
                {t('Copia la chiave')}
              </Button>
            </div>
          </>
        )}
        <TextField label={t('Ramo')} value={ramo} onChange={(e) => setRamo(e.target.value)} />
        {salva.error && <Banner tone="error" title={t('Non l’ho cambiato')}>{(salva.error as Error).message}</Banner>}
        {cambiato && (
          <div className="md-stack__inline">
            <Button
              variant="tonal"
              icon={salva.isPending ? <Anello /> : undefined}
              disabled={salva.isPending || !ramo.trim() || (cartella !== '' && !url.trim())}
              onClick={() => salva.mutate()}
            >
              {cartella ? t('Usa questo repository') : t('Usa questo ramo')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Il nome con cui mostrare una cartella senza git: quello del repository se
 * è una copia di CloudPepper (`<repository>.git-<id>`), l'ultima parte del
 * percorso se no.
 */
export function nomeDellaCartella(dir: string): string {
  const parti = dir.split('/').filter(Boolean);
  for (const p of parti) {
    const m = /^(.+)\.git-[0-9a-f]+$/.exec(p);
    if (m) return m[1]!;
  }
  return parti[parti.length - 1] ?? dir;
}

function Librerie({ slug, tr }: { slug: string; tr: Trasloco }) {
  const qc = useQueryClient();
  const campo = useId();
  const [testo, setTesto] = useState((tr.spec.libraries ?? []).join('\n'));
  const originale = (tr.spec.libraries ?? []).join('\n');
  useEffect(() => setTesto(originale), [originale]);
  const salva = useMutation({
    mutationFn: () =>
      must(api.PATCH('/api/v1/orgs/{slug}/migrations/{id}', {
        params: { path: { slug, id: tr.id } },
        body: { libraries: testo.split('\n').map((l) => l.trim()).filter(Boolean) },
      })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['migration', slug, tr.id] }),
  });
  return (
    <section className="md-stack">
      <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Librerie Python')}</h2>
      <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Quelle installate a mano su CloudPepper e che Odoo non porta, lette dall’ambiente dell’istanza. Togli quelle che non servono più.')}
      </p>
      <div className="md-field">
        <label htmlFor={campo}>{t('Librerie, una per riga')}</label>
        <textarea id={campo} rows={Math.max(2, Math.min(8, testo.split('\n').length + 1))} spellCheck={false} value={testo} onChange={(e) => setTesto(e.target.value)} />
      </div>
      {salva.error && <Banner tone="error" title={t('Non le ho salvate')}>{(salva.error as Error).message}</Banner>}
      {testo !== originale && (
        <div className="md-stack__inline">
          <Button variant="tonal" disabled={salva.isPending} onClick={() => salva.mutate()}>{t('Salva le librerie')}</Button>
        </div>
      )}
    </section>
  );
}

/** «Trasloca»: cosa succede, detto prima di farlo. */
function ConfermaTrasloco({ slug, tr, aperto, chiudi }: { slug: string; tr: Trasloco; aperto: boolean; chiudi: () => void }) {
  const trasloca = useAzione(slug, tr.id, 'cutover', t('Parte il trasloco'));
  return (
    <Dialog
      open={aperto}
      onOpenChange={(v) => !v && chiudi()}
      title={t('Trasloca {nome}', { nome: tr.name })}
      description={t('Il giorno vero: avvisa il cliente prima.')}
      actions={
        <>
          <Button variant="text" onClick={chiudi}>{t('Annulla')}</Button>
          <Button
            variant="filled"
            icon={trasloca.isPending ? <Anello /> : undefined}
            disabled={trasloca.isPending}
            onClick={() => trasloca.mutate(undefined, { onSuccess: chiudi })}
          >
            {t('Trasloca adesso')}
          </Button>
        </>
      }
    >
      <div className="md-stack">
        <ol className="md-body-medium" style={{ margin: 0, paddingInlineStart: '1.2em' }}>
          <li>
            {t('Molo {spegne} e la tiene spenta: da qui nessuno ci scrive più.', {
              spegne: <b>{t('spegne {istanza} su CloudPepper', { istanza: tr.sourceInstance })}</b>,
            })}
          </li>
          <li>
            {t('Rifà il dump con i dati di adesso e crea {nome} su {server}, {senza}: cron, posta e pagamenti accesi.', {
              nome: <b>{tr.name}</b>,
              server: tr.serverName,
              senza: <b>{t('senza neutralizzarla')}</b>,
            })}
          </li>
          <li>{t('La prova se ne va.')}</li>
        </ol>
        {tr.trialSeconds ? (
          <p className="md-body-medium" style={{ margin: 0 }}>{t('Il fermo stimato dalla prova è di {durata}.', { durata: <b>{durata(tr.trialSeconds)}</b> })}</p>
        ) : null}
        <p className="md-body-small" style={{ margin: 0 }}>
          {t('Se qualcosa va storto, «Torna su CloudPepper» riaccende l’originale e rimette il nome dove puntava.')}
        </p>
        {trasloca.error && <Banner tone="error" title={t('Non è partito')}>{(trasloca.error as Error).message}</Banner>}
      </div>
    </Dialog>
  );
}

/** Il trasloco vero si è fermato: si riprova, o si torna su CloudPepper. */
function TraslocoFermo({ slug, tr }: { slug: string; tr: Trasloco }) {
  const riprova = useAzione(slug, tr.id, 'cutover', t('Riprovo'));
  const [torna, setTorna] = useState(false);
  return (
    <section className="md-stack">
      <p className="md-body-medium" style={{ margin: 0 }}>
        {tr.sourceStopped
          ? t('L’originale su CloudPepper è spento. Se il motivo è un repository o un modulo, sistemalo e riprova: lo zip è ancora sul server, non si rifà il dump.')
          : t('L’originale su CloudPepper è ancora acceso.')}
      </p>
      {riprova.error && <Banner tone="error" title={t('Non è ripartito')}>{(riprova.error as Error).message}</Banner>}
      <div className="md-stack__inline">
        <Button variant="filled" icon={riprova.isPending ? <Anello /> : undefined} disabled={riprova.isPending} onClick={() => riprova.mutate()}>
          {t('Riprova')}
        </Button>
        <Button variant="text" onClick={() => setTorna(true)}>{t('Torna su CloudPepper')}</Button>
      </div>
      <TornaSuCloudPepper slug={slug} tr={tr} aperto={torna} chiudi={() => setTorna(false)} />
    </section>
  );
}

function TornaSuCloudPepper({ slug, tr, aperto, chiudi }: { slug: string; tr: Trasloco; aperto: boolean; chiudi: () => void }) {
  const torna = useAzione(slug, tr.id, 'revert', t('Torno su CloudPepper'));
  return (
    <Dialog
      open={aperto}
      onOpenChange={(v) => !v && chiudi()}
      title={t('Torna su CloudPepper')}
      description={t('L’istanza su Molo se ne va, il nome torna dove puntava, l’originale si riaccende.')}
      actions={
        <>
          <Button variant="text" onClick={chiudi}>{t('Annulla')}</Button>
          <Button variant="filled" icon={torna.isPending ? <Anello /> : undefined} disabled={torna.isPending} onClick={() => torna.mutate(undefined, { onSuccess: chiudi })}>
            {t('Torna su CloudPepper')}
          </Button>
        </>
      }
    >
      <p className="md-body-medium" style={{ margin: 0 }}>
        {t('Quello che qualcuno ha scritto su Molo dopo il trasloco si perde. Il prossimo tentativo comincia da una prova nuova.')}
      </p>
      {torna.error && <Banner tone="error" title={t('Non sono tornato')}>{(torna.error as Error).message}</Banner>}
    </Dialog>
  );
}

/**
 * Gli indirizzi: dove punta ogni nome, e cosa fare. Prima del trasloco è il
 * consiglio sul TTL; dopo, i nomi del cliente che puntano già qui si
 * collegano all'istanza da soli.
 */
function Indirizzi({ slug, tr, collega }: { slug: string; tr: Trasloco; collega?: boolean }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [torna, setTorna] = useState(false);
  const q = useQuery({
    queryKey: ['migration-addresses', slug, tr.id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/migrations/{id}/addresses', { params: { path: { slug, id: tr.id } } })),
    refetchInterval: collega ? 15_000 : false,
  });
  const collegali = useMutation({
    mutationFn: () => must(api.POST('/api/v1/orgs/{slug}/migrations/{id}/addresses', { params: { path: { slug, id: tr.id } } })),
    onSuccess: (r) => qc.setQueryData(['migration-addresses', slug, tr.id], r),
  });
  const avanti = useAzione(slug, tr.id, 'advance');
  const dati = collegali.data ?? q.data;
  const voci = dati?.items ?? [];

  return (
    <div className="md-stack">
      {q.error && <Banner tone="error" title={t('Non riesco a leggere gli indirizzi')}>{q.error.message}</Banner>}
      {q.isPending && <Skeleton height="5rem" />}
      {voci.length === 0 && dati && (
        <p className="md-body-medium" style={{ margin: 0 }}>{t('L’istanza non aveva indirizzi suoi su CloudPepper.')}</p>
      )}
      {voci.length > 0 && (
        <List>
          {voci.map((v) => (
            <ListItem
              key={v.hostname}
              headline={<span className="md-mono">{v.hostname}</span>}
              supporting={spiegazione(v, dati?.serverIp ?? '', !!collega)}
              trailing={<StatoIndirizzo v={v} />}
            />
          ))}
        </List>
      )}
      {collegali.error && <Banner tone="error" title={t('Non li ho collegati')}>{(collegali.error as Error).message}</Banner>}
      {collega && (
        <>
          <div className="md-stack__inline">
            <Button
              variant="tonal"
              icon={collegali.isPending ? <Anello /> : undefined}
              disabled={collegali.isPending}
              onClick={() => collegali.mutate(undefined, { onSuccess: () => avvisa(t('Indirizzi controllati')) })}
            >
              {t('Controlla e collega')}
            </Button>
            <Button variant="filled" disabled={avanti.isPending} onClick={() => avanti.mutate()}>{t('Avanti ai controlli')}</Button>
            <Button variant="text" onClick={() => setTorna(true)}>{t('Torna su CloudPepper')}</Button>
          </div>
          {avanti.error && <Banner tone="error" title={t('Non sono andato avanti')}>{(avanti.error as Error).message}</Banner>}
          <TornaSuCloudPepper slug={slug} tr={tr} aperto={torna} chiudi={() => setTorna(false)} />
        </>
      )}
    </div>
  );
}

function spiegazione(v: Indirizzo, serverIp: string, dopo: boolean): string {
  const dove = (v.addrs ?? []).length > 0 ? t('punta a {indirizzi}', { indirizzi: (v.addrs ?? []).join(', ') }) : t('non si risolve');
  const ttl = v.ttl && v.ttl > 3600
    ? ` · ${t('TTL {durata}: abbassalo il giorno prima, o il cambio si vedrà tardi', { durata: durata(v.ttl) })}`
    : '';
  if (v.platform) {
    return t('Sotto il dominio dell’organizzazione: lo sposta Molo creando l’istanza. Adesso {dove}.', { dove });
  }
  if (v.attached && v.status === 'error') {
    return v.error
      ? t('Il certificato non è arrivato: {errore}. Quando punta qui, «Controlla e collega» riprova.', { errore: v.error })
      : t('Il certificato non è arrivato. Quando punta qui, «Controlla e collega» riprova.');
  }
  if (v.error) return `${dove} · ${v.error}`;
  if (v.attached && v.status === 'pending') return t('Collegato: il certificato sta arrivando.');
  if (v.attached) return t('Collegato all’istanza, con il suo certificato.');
  if (v.pointsHere) return dopo ? t('Punta già qui: «Controlla e collega» lo collega.') : t('Punta già al server di destinazione.');
  return t('Il cliente deve cambiare il record A in {ip}: adesso {dove}{ttl}.', { ip: serverIp || t('l’IP del server'), dove, ttl });
}

function StatoIndirizzo({ v }: { v: Indirizzo }) {
  if (v.attached && v.status === 'error') return <StatusChip status="error" label={t('Non va')} tone="error" />;
  if (v.attached && v.status === 'pending') return <StatusChip status="pending" label={t('Certificato in arrivo')} tone="running" />;
  if (v.attached) return <StatusChip status="ok" label={t('Collegato')} tone="success" />;
  if (v.pointsHere) return <StatusChip status="here" label={t('Punta qui')} tone="running" />;
  if (v.platform) return <StatusChip status="molo" label={t('Lo sposta Molo')} tone="neutral" />;
  return <StatusChip status="todo" label={t('Da puntare')} tone="neutral" />;
}

/** I controlli: quello che verifica Molo, quello che solo una persona sa. */
function Controlli({ slug, tr }: { slug: string; tr: Trasloco }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const q = useQuery({
    queryKey: ['migration-checks', slug, tr.id],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/migrations/{id}/checks', { params: { path: { slug, id: tr.id } } })).then((r) => r.checks ?? []),
    refetchInterval: 15_000,
  });
  const conferma = useMutation({
    mutationFn: (v: { usersOk: boolean; integrationsOk: boolean }) =>
      must(api.POST('/api/v1/orgs/{slug}/migrations/{id}/confirm', { params: { path: { slug, id: tr.id } }, body: v })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['migration', slug, tr.id] }),
  });
  const backup = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/instances/{id}/backups', { params: { path: { slug, id: tr.instance!.id } } })),
    onSuccess: () => avvisa(t('Backup partito: il controllo diventa verde quando finisce')),
  });
  const chiudi = useAzione(slug, tr.id, 'close', t('Trasloco chiuso'));
  const controlli = q.data ?? [];
  const bloccano = controlli.filter((c) => c.blocking && c.outcome !== 'ok');

  return (
    <>
      <section className="md-stack">
        <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Cosa ho verificato')}</h2>
        {q.error && <Banner tone="error" title={t('Non riesco a fare i controlli')}>{q.error.message}</Banner>}
        {q.isPending && <Skeleton height="10rem" />}
        {controlli.length > 0 && (
          <List>
            {controlli.map((c) => <VoceControllo key={c.key} c={c} />)}
          </List>
        )}
        {controlli.some((c) => c.key === 'backup' && c.outcome !== 'ok') && tr.instance && (
          <div className="md-stack__inline">
            <Button variant="tonal" icon={backup.isPending ? <Anello /> : undefined} disabled={backup.isPending} onClick={() => backup.mutate()}>
              {t('Fai un backup adesso')}
            </Button>
          </div>
        )}
        {backup.error && <Banner tone="error" title={t('Il backup non è partito')}>{(backup.error as Error).message}</Banner>}
        {controlli.some((c) => c.key === 'webhook' && c.outcome !== 'ok') && tr.instance && <Webhook slug={slug} istanza={tr.instance.id} />}
      </section>

      <section className="md-stack">
        <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Cosa sai solo tu')}</h2>
        <Checkbox
          checked={tr.usersOk}
          disabled={conferma.isPending}
          onCheckedChange={(v) => conferma.mutate({ usersOk: v === true, integrationsOk: tr.integrationsOk })}
          label={t('Gli utenti entrano con le loro credenziali e trovano i loro dati')}
        />
        <Checkbox
          checked={tr.integrationsOk}
          disabled={conferma.isPending}
          onCheckedChange={(v) => conferma.mutate({ usersOk: tr.usersOk, integrationsOk: v === true })}
          label={t('Le integrazioni funzionano: fatturazione elettronica, banche, pagamenti, posta')}
        />
        {conferma.error && <Banner tone="error" title={t('Non l’ho registrato')}>{(conferma.error as Error).message}</Banner>}
      </section>

      {chiudi.error && <Banner tone="error" title={t('Non l’ho chiuso')}>{(chiudi.error as Error).message}</Banner>}
      <div className="md-stack__inline">
        <Button
          variant="filled"
          icon={chiudi.isPending ? <Anello /> : undefined}
          disabled={chiudi.isPending || bloccano.length > 0 || !tr.usersOk || !tr.integrationsOk}
          onClick={() => chiudi.mutate()}
        >
          {t('Chiudi il trasloco')}
        </Button>
      </div>
    </>
  );
}

function VoceControllo({ c }: { c: Controllo }) {
  const tono = c.outcome === 'ok' ? 'success' : c.outcome === 'fail' ? 'error' : 'running';
  const parola = c.outcome === 'ok' ? t('A posto') : c.outcome === 'fail' ? (c.blocking ? t('Blocca') : t('No')) : t('Da guardare');
  return <ListItem headline={c.title} supporting={c.detail} trailing={<StatusChip status={c.outcome} label={parola} tone={tono} />} />;
}

/**
 * I webhook dei repository collegati dal trasloco: il loro segreto è nato
 * dentro un job e non l'ha visto nessuno, quindi se ne fa uno nuovo e si
 * mostra una volta, con quello che va incollato su GitHub.
 */
function Webhook({ slug, istanza }: { slug: string; istanza: string }) {
  const avvisa = useSnackbar();
  const [creato, setCreato] = useState<RepoCreato | null>(null);
  const repos = useQuery({
    queryKey: ['instance-addons', slug, istanza],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/instances/{id}/addons', { params: { path: { slug, id: istanza } } })).then((r) => r ?? []),
  });
  const nuovo = useMutation({
    mutationFn: (repoId: string) =>
      must(api.POST('/api/v1/orgs/{slug}/instances/{id}/addons/{repoId}/webhook', { params: { path: { slug, id: istanza, repoId } } })),
    onSuccess: (r) => setCreato(r),
  });
  const indirizzo = (id: string) => `${window.location.origin}/hooks/git/${id}`;
  const copia = (s: string, fatto: string) => { void navigator.clipboard?.writeText(s); avvisa(fatto); };
  return (
    <Card variant="outlined">
      <div className="md-stack">
        <p className="md-body-medium" style={{ margin: 0 }}>
          {t('Perché una push su GitHub arrivi all’istanza, ogni repository vuole il suo webhook. Su GitHub: {percorso}, indirizzo e segreto qui sotto, tipo di contenuto {tipo}. Togli quello di CloudPepper, e la sua deploy key.', {
            percorso: <b>Settings → Webhooks → Add webhook</b>,
            tipo: <b>application/json</b>,
          })}
        </p>
        {(repos.data ?? []).map((r) => (
          <div key={r.id} className="md-stack__inline" style={{ alignItems: 'center' }}>
            <span className="md-mono md-body-small" style={{ flex: 1 }}>{r.url}</span>
            <Button variant="text" disabled={nuovo.isPending} onClick={() => nuovo.mutate(r.id)}>{t('Mostra il webhook')}</Button>
          </div>
        ))}
        {nuovo.error && <Banner tone="error" title={t('Non l’ho generato')}>{(nuovo.error as Error).message}</Banner>}
      </div>
      <Dialog
        open={!!creato}
        onOpenChange={(v) => !v && setCreato(null)}
        title={t('Il webhook')}
        description={t('Il segreto si mostra una volta sola: quello di prima non vale più.')}
        actions={<Button variant="filled" onClick={() => setCreato(null)}>{t('Fatto')}</Button>}
      >
        {creato && (
          <div className="md-stack">
            <span className="md-body-small">{t('Indirizzo')}</span>
            <pre className="md-log md-code">{indirizzo(creato.id)}</pre>
            <Button variant="text" icon={<Copy />} onClick={() => copia(indirizzo(creato.id), t('Indirizzo copiato'))}>{t('Copia l’indirizzo')}</Button>
            <span className="md-body-small">{t('Segreto')}</span>
            <pre className="md-log md-code">{creato.webhookSecret}</pre>
            <Button variant="text" icon={<Copy />} onClick={() => copia(creato.webhookSecret, t('Segreto copiato'))}>{t('Copia il segreto')}</Button>
          </div>
        )}
      </Dialog>
    </Card>
  );
}

function Fatto({ slug, tr }: { slug: string; tr: Trasloco }) {
  const navigate = useNavigate();
  return (
    <>
      <Banner
        tone="success"
        title={t('{nome} è su Molo', { nome: tr.name })}
        action={tr.instance && (
          <Button variant="text" onClick={() => navigate({ to: '/orgs/$slug/instances/$id', params: { slug, id: tr.instance!.id } })}>
            {t('Apri l’istanza')}
          </Button>
        )}
      >
        {tr.finishedAt
          ? t('Traslocata il {data}.', { data: new Date(tr.finishedAt).toLocaleDateString(localeIntl()) })
          : t('Traslocata.')}{' '}
        {t('Se su CloudPepper c’era una copia di prova, rifalla da qui: «Crea una copia di prova» nella scheda dell’istanza.')}
      </Banner>
      <Details summary={t('Resta da fare fuori da Molo')}>
        <ul className="md-body-small" style={{ margin: 0, paddingInlineStart: '1.2em' }}>
          <li>{t('Cancellare {istanza} dal pannello di CloudPepper: su Molo è spenta e mascherata, ma lì risulta ancora.', { istanza: tr.sourceInstance })}</li>
          <li>
            {t('Quando la macchina {macchina} è vuota, scollegarla da {scheda} e dismetterla presso il suo provider.', {
              macchina: tr.sourceName,
              scheda: (
                <a href="#" onClick={(e) => { e.preventDefault(); navigate({ to: '/orgs/$slug/traslochi/sorgenti/$id', params: { slug, id: tr.sourceId } }); }}>
                  {t('la sua scheda')}
                </a>
              ),
            })}
          </li>
        </ul>
      </Details>
    </>
  );
}
